import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getApiErrorMessage, rankingsApi } from "../api";
import { RankingUser } from "../types";
import { useGuardedAsyncAction } from "../hooks/useGuardedAsyncAction";

const generateMonthlyPoints = (
  eventAcquiredPoints: Array<{
    event_name: string;
    point: number;
    date: string;
  }>,
) => {
  const monthlyMap = new Map<string, number>();
  eventAcquiredPoints.forEach((item) => {
    const date = new Date(item.date);
    const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    monthlyMap.set(month, (monthlyMap.get(month) || 0) + item.point);
  });
  return Array.from(monthlyMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, points]) => ({ month, points }));
};

interface RankingsContextValue {
  rankings: RankingUser[];
  isLoading: boolean;
  error: string | null;
  fetchAll: () => Promise<void>;
  lastFetchedAt: number | null;
}

const RankingsContext = createContext<RankingsContextValue | null>(null);

export const RankingsProvider: React.FC<{
  isAuthenticated: boolean;
  children: React.ReactNode;
}> = ({ isAuthenticated, children }) => {
  const [rankings, setRankings] = useState<RankingUser[]>([]);
  const { isLoading, error, lastFetchedAt, run, setLastFetchedAt, setError } =
    useGuardedAsyncAction();

  const fetchAll = useCallback(async () => {
    await run(
      async () => {
        const response = await rankingsApi.getYearly();
        setRankings(
          response.rankings
            .map((r) => ({
              rank: r.rank,
              name: r.name,
              points: r.point,
              isMe: r.is_login_member,
              monthlyPoints: generateMonthlyPoints(r.event_acquired_points),
              eventAcquiredPoints: r.event_acquired_points,
            }))
            .sort((a, b) => a.rank - b.rank),
        );
      },
      { onErrorMessage: getApiErrorMessage },
    );
  }, [run]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAll();
    } else {
      setRankings([]);
      setLastFetchedAt(null);
      setError(null);
    }
  }, [fetchAll, isAuthenticated, setError, setLastFetchedAt]);

  return (
    <RankingsContext.Provider
      value={{ rankings, isLoading, error, fetchAll, lastFetchedAt }}
    >
      {children}
    </RankingsContext.Provider>
  );
};

export const useRankingsContext = (): RankingsContextValue => {
  const ctx = useContext(RankingsContext);
  if (!ctx)
    throw new Error("useRankingsContext must be used within RankingsProvider");
  return ctx;
};
